/**
 * Which scenario the viability check runs on — said out loud and changeable.
 *
 * Before this the app silently used the active scenario (roadmap P-10).
 * Choosing here is the same act as picking a scenario in the planning
 * engine (`activateScenario`), so the app and /planning/engine never
 * disagree about which assumptions are in play.
 */

import { Field, Select } from '@/components/ds';
import type { Scenario } from '@/lib/planningEngineModule.types';
import { AppCard, Eyebrow } from '../components/primitives';

export function ViabilityScenarioChoice({
  scenario,
  scenarios,
  onChoose,
  disabled,
  error,
}: {
  scenario: Scenario | null;
  scenarios: Scenario[];
  onChoose: (scenarioId: string) => void;
  disabled?: boolean;
  error?: string | null;
}) {
  if (!scenario) return null;

  if (scenarios.length < 2) {
    return (
      <AppCard>
        <Eyebrow>Scenario</Eyebrow>
        <p className="mt-2 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
          Running on “{scenario.name}”, the only scenario in this workspace.
        </p>
      </AppCard>
    );
  }

  return (
    <AppCard>
      <Field
        label={<Eyebrow>Scenario</Eyebrow>}
        hint="The check runs on this scenario’s assumptions. Choosing another makes it the active scenario in planning too."
        error={error ?? undefined}
      >
        <Select
          value={scenario.id}
          disabled={disabled}
          onChange={(e) => onChoose(e.target.value)}
          options={scenarios.map((s) => ({ value: s.id, label: s.name }))}
          aria-label="Scenario"
        />
      </Field>
    </AppCard>
  );
}
